import { useRef, useState } from "react";
import { Plus, Trash2, GraduationCap, ImagePlus, Quote, Save, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { fileToResizedDataUrl } from "../lib/imageUtils";

export function TeachingPanel({ open, onOpenChange, teachingText, setTeachingText, teachingExamples, setTeachingExamples, onSaveNewProfile, onUpdateActiveProfile, activeProfileName }) {
  const [hint, setHint] = useState("");
  const [exText, setExText] = useState("");
  const [exImg, setExImg] = useState(null);
  const [profileName, setProfileName] = useState("");
  const fileRef = useRef(null);

  const addHint = () => {
    if (!hint.trim()) return;
    setTeachingText([...teachingText, hint.trim()]);
    setHint("");
  };
  const addExample = () => {
    if (!exImg || !exText.trim()) {
      toast.error("Voeg een afbeelding én de vertaling toe");
      return;
    }
    setTeachingExamples([...teachingExamples, { image_base64: exImg, text: exText.trim() }]);
    setExImg(null);
    setExText("");
    toast.success("Referentievoorbeeld toegevoegd");
  };
  const pickImage = async (file) => {
    if (!file) return;
    setExImg(await fileToResizedDataUrl(file, 1400, 0.9));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto bg-[var(--paper)] sm:max-w-2xl" data-testid="teaching-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl text-[var(--ink)]">
            <GraduationCap className="h-6 w-6 text-[var(--brand)]" /> Handschrift leren
          </DialogTitle>
          <DialogDescription className="text-[var(--ink-2)]">
            Leer de AI dit specifieke handschrift lezen met voorbeeldzinnen en bevestigde fragmenten.
          </DialogDescription>
        </DialogHeader>

        {/* Save as writer profile */}
        <section className="mt-2 rounded-xl border border-[var(--brand)]/30 bg-[var(--surface-muted)] p-3">
          <h3 className="mb-2 flex items-center gap-2 font-display text-sm font-semibold text-[var(--ink)]">
            <Save className="h-4 w-4 text-[var(--brand)]" /> Schrijverprofiel
          </h3>
          <p className="mb-2 text-xs text-[var(--ink-2)]">
            Bewaar deze leeshulp als profiel per schrijver, zodat nieuwe brieven van dezelfde hand meteen beter gelezen worden.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
              placeholder="Naam schrijver, bijv. Oma Cornelia"
              data-testid="profile-name-input"
              className="h-9 flex-1 rounded-md border border-[var(--border-subtle)] bg-[var(--paper)] px-3 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]"
            />
            <button
              onClick={() => {
                if (!profileName.trim()) return toast.error("Geef het profiel een naam");
                onSaveNewProfile(profileName.trim());
                setProfileName("");
              }}
              data-testid="save-profile-btn"
              className="flex items-center gap-1.5 rounded-md bg-[var(--brand)] px-3 py-2 text-sm font-semibold text-white hover:bg-[var(--brand-hover)]"
            >
              <Save className="h-4 w-4" /> Nieuw profiel
            </button>
            {activeProfileName && (
              <button
                onClick={onUpdateActiveProfile}
                data-testid="update-profile-btn"
                className="flex items-center gap-1.5 rounded-md border border-[var(--brand)] px-3 py-2 text-sm font-semibold text-[var(--brand)] hover:bg-[var(--surface)]"
              >
                <RefreshCw className="h-4 w-4" /> "{activeProfileName}" bijwerken
              </button>
            )}
          </div>
        </section>

        {/* Text hints */}
        <section className="mt-2">
          <h3 className="mb-2 flex items-center gap-2 font-display text-sm font-semibold text-[var(--ink)]">
            <Quote className="h-4 w-4 text-[var(--brand)]" /> Voorbeeldzinnen & leeshulp
          </h3>
          <div className="flex gap-2">
            <textarea
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              placeholder='Bijv. "De eerste regel luidt: Lieve moeder, ik schrijf u uit Den Haag…"'
              data-testid="teach-hint-input"
              className="min-h-[60px] flex-1 resize-y rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)] p-3 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]"
            />
            <button onClick={addHint} data-testid="teach-hint-add" className="self-start rounded-lg bg-[var(--brand)] p-3 text-white hover:bg-[var(--brand-hover)]">
              <Plus className="h-4 w-4" />
            </button>
          </div>
          {teachingText.length > 0 && (
            <ul className="mt-3 space-y-2">
              {teachingText.map((t, i) => (
                <li key={i} className="flex items-start gap-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)] p-2.5 text-sm text-[var(--ink-2)]" data-testid={`teach-hint-${i}`}>
                  <span className="flex-1">{t}</span>
                  <button onClick={() => setTeachingText(teachingText.filter((_, j) => j !== i))} className="text-[var(--ink-3)] hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Reference examples */}
        <section className="mt-5">
          <h3 className="mb-2 flex items-center gap-2 font-display text-sm font-semibold text-[var(--ink)]">
            <ImagePlus className="h-4 w-4 text-[var(--brand)]" /> Bevestigde fragmenten (afbeelding + tekst)
          </h3>
          <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)] p-3">
            <div className="flex gap-3">
              <button
                onClick={() => fileRef.current?.click()}
                data-testid="teach-example-image-btn"
                className="flex h-24 w-32 shrink-0 items-center justify-center overflow-hidden rounded-md border-2 border-dashed border-[var(--border-active)] text-[var(--ink-3)] hover:border-[var(--brand)]"
              >
                {exImg ? <img src={exImg} alt="voorbeeld" className="h-full w-full object-cover" /> : <ImagePlus className="h-6 w-6" />}
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickImage(e.target.files?.[0])} />
              <div className="flex flex-1 flex-col gap-2">
                <textarea
                  value={exText}
                  onChange={(e) => setExText(e.target.value)}
                  placeholder="Typ wat er in dit fragment staat…"
                  data-testid="teach-example-text"
                  className="min-h-[60px] flex-1 resize-y rounded-md border border-[var(--border-subtle)] bg-[var(--paper)] p-2.5 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]"
                />
                <button onClick={addExample} data-testid="teach-example-add" className="self-end rounded-md bg-[var(--brand-2)] px-4 py-2 text-sm font-semibold text-white hover:brightness-110">
                  Toevoegen
                </button>
              </div>
            </div>
          </div>
          {teachingExamples.length > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {teachingExamples.map((ex, i) => (
                <div key={i} className="group relative overflow-hidden rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)]" data-testid={`teach-example-${i}`}>
                  <img src={ex.image_base64} alt="fragment" className="h-20 w-full object-cover" />
                  <p className="p-2 font-hand text-sm leading-tight text-[var(--ink)]">{ex.text}</p>
                  <button
                    onClick={() => setTeachingExamples(teachingExamples.filter((_, j) => j !== i))}
                    className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </DialogContent>
    </Dialog>
  );
}
